import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { User, Phone, HelpCircle, Sparkles, LayoutGrid, Inbox, Settings2 } from "lucide-react";
import { Reveal, SectionLabel, MaskLines } from "./Reveal";
import { DEMO_LEADS, TONE } from "../../lib/demoLeads";
import { useContent } from "../../lib/content";

const FILTERS = ["Tutti", "Pompe di calore", "Climatizzazione", "Fotovoltaico"];

const Compare = () => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-1 gap-4">
    <Reveal className="rounded-2xl border hairline bg-white/[0.02] p-5" data-testid="compare-generic">
      <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-4">Contatto generico</p>
      <div className="space-y-2.5 text-[14px]">
        <p className="flex items-center gap-2.5 text-slate-300"><User className="w-4 h-4 text-slate-500" />Nome Cognome</p>
        <p className="flex items-center gap-2.5 text-slate-300"><Phone className="w-4 h-4 text-slate-500" />333 •••• •••</p>
        {["Cosa vuole installare?", "Dove si trova?", "Quando?"].map((q) => (
          <p key={q} className="flex items-center gap-2.5 text-slate-400"><HelpCircle className="w-4 h-4 text-slate-500" />{q}</p>
        ))}
      </div>
    </Reveal>
    <Reveal delay={0.1} className="relative rounded-2xl p-5 glass border-[#ff5500]/30 overflow-hidden" data-testid="compare-qualified">
      <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#ff5500]/20 blur-3xl" />
      <p className="relative font-mono text-[11px] uppercase tracking-[0.2em] text-[#ff8a4c] mb-4 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" />Richiesta qualificata</p>
      <dl className="relative grid grid-cols-[auto,1fr] gap-x-4 gap-y-2 text-[14px]">
        {[["Settore", "Pompe di calore"], ["Interesse", "Installazione nuovo impianto"], ["Immobile", "Villetta indipendente"], ["Zona", "Napoli"], ["Sopralluogo", "Gio 10:30, in agenda"]].map(([k, v]) => (
          <div key={k} className="contents"><dt className="text-slate-400">{k}</dt><dd className="text-white">{v}</dd></div>
        ))}
      </dl>
    </Reveal>
  </div>
);

const Row = ({ l }) => {
  const t = TONE[l.tone] || TONE.heat;
  return (
    <motion.div layout initial={{ opacity: 0, y: -16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`demo-lead-${l.key}`}
      className="rounded-xl border hairline bg-white/[0.025] hover:bg-white/[0.05] transition-colors p-4 grid grid-cols-2 md:grid-cols-[1.1fr,1.4fr,0.8fr,1fr,1.1fr] gap-x-4 gap-y-2 items-center">
      <div className="flex items-center gap-2.5 col-span-2 md:col-span-1">
        <span className={`w-2 h-8 rounded-full ${t.bg}`} />
        <div><p className={`text-[13px] font-semibold ${t.text}`}>{l.sector}</p><p className="font-mono text-[10px] text-slate-400">#{l.id}</p></div>
      </div>
      <p className="text-[13px] text-slate-100 col-span-2 md:col-span-1">{l.interest}</p>
      <p className="text-[13px] text-slate-300">{l.zone}</p>
      <p className="text-[13px] text-slate-300">{l.property} · {l.timing}</p>
      <span className="col-span-2 md:col-span-1 justify-self-start text-[11px] px-2 py-1 rounded-full bg-[#ff5500]/12 text-[#ffb07f] border border-[#ff5500]/30">{l.status}</span>
    </motion.div>
  );
};

const Dashboard = () => {
  const [filter, setFilter] = useState("Tutti");
  const [rows, setRows] = useState(() => DEMO_LEADS.slice(0, 4).map((l, i) => ({ ...l, key: i })));
  useEffect(() => {
    let n = 4;
    const id = setInterval(() => {
      const next = { ...DEMO_LEADS[n % DEMO_LEADS.length], key: n };
      n += 1;
      setRows((r) => [next, ...r].slice(0, 6));
    }, 4200);
    return () => clearInterval(id);
  }, []);
  const shown = rows.filter((r) => filter === "Tutti" || r.sector === filter).slice(0, 4);

  return (
    <div data-testid="demo-dashboard" className="relative glass rounded-3xl overflow-hidden">
      <div className="flex items-center justify-between px-4 sm:px-5 h-12 border-b hairline">
        <div className="flex gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#ff5500]/70" /><span className="w-2.5 h-2.5 rounded-full bg-[#4fc3f7]/70" /><span className="w-2.5 h-2.5 rounded-full bg-[#3b6ef0]/70" /></div>
        <span data-testid="demo-dashboard-badge" className="font-mono text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded-md bg-[#4fc3f7]/10 text-[#7fd6fb] border border-[#4fc3f7]/25">Esempio grafico dimostrativo</span>
      </div>
      <div className="grid md:grid-cols-[56px,1fr]">
        <div className="hidden md:flex flex-col items-center gap-4 py-5 border-r hairline text-slate-400">
          <LayoutGrid className="w-4 h-4" /><Inbox className="w-4 h-4 text-[#ff8a4c]" /><Settings2 className="w-4 h-4" />
        </div>
        <div className="p-4 sm:p-5">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <p className="text-white font-semibold mr-2 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-[#ff5500] pulse-dot" />Richieste qualificate</p>
            {FILTERS.map((f) => (
              <button key={f} data-testid={`demo-filter-${f.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setFilter(f)}
                className={`text-[12px] px-3 py-1.5 rounded-full border transition-colors ${filter === f ? "bg-white text-[#060a14] border-white" : "hairline text-slate-300 hover:text-white"}`}>{f}</button>
            ))}
          </div>
          <div className="hidden md:grid grid-cols-[1.1fr,1.4fr,0.8fr,1fr,1.1fr] gap-4 px-4 pb-2 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-400">
            <span>Settore</span><span>Interesse</span><span>Zona</span><span>Immobile · Tempi</span><span>Stato</span>
          </div>
          <div className="space-y-2 min-h-[340px]">
            {shown.map((l) => <Row key={l.key} l={l} />)}
          </div>
        </div>
      </div>
    </div>
  );
};

export const LeadsShowcase = () => {
  const h = useContent().home;
  return (
    <section id="contatti-qualificati" data-testid="leads-section" className="relative py-24 sm:py-32 lg:py-40 overflow-hidden">
      <div className="absolute right-[-15%] top-1/3 w-[600px] h-[600px] rounded-full bg-[#3b6ef0]/15 blur-[150px]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Reveal><SectionLabel index="03" tone="heat">{h.leads_label}</SectionLabel></Reveal>
            <h2 className="mt-7 font-display font-extrabold text-white text-3xl sm:text-5xl lg:text-[3.6rem] leading-[1.02]">
              <MaskLines text={h.leads_title.replace(". ", ".\n")} lineClass={(i) => (i === 1 ? "text-heat-grad" : "")} />
            </h2>
          </div>
          <Reveal delay={0.15} className="lg:col-span-5"><p className="text-slate-300 leading-relaxed">{h.leads_text}</p></Reveal>
        </div>
        <div className="mt-14 grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4"><Compare /></div>
          <Reveal delay={0.1} className="lg:col-span-8"><Dashboard /></Reveal>
        </div>
        <p className="mt-5 text-xs text-slate-400">Schermate illustrative: non è un software fornito ai clienti.</p>
      </div>
    </section>
  );
};
